/* eslint-disable max-lines */
import {
  Dialog,
  DialogContent
} from 'shared/components/ui/dialog';
import {
  Label
} from 'shared/components/ui/label';
import {
  Input
} from 'shared/components/ui/input';
import React, {
  useContext, useEffect, useState
} from 'react';
import type {
  JSX
} from 'react';
import {
  useParams
} from 'react-router-dom';
import {
  Context
} from 'shared/context/GlobalState';
import {
  channelService
} from 'shared/api';
import AssistantSelect from './AssistantSelect';
import EmailModeSettings, {
  EmailMode,
  EmailReplyBehavior,
  emailModeFromFields,
  emailModeToFields
} from './EmailModeSettings';
import {
  Channel, RedisChannelUpdate
} from '../types/builderChannel.types';
import {
  ChannelSetupLayout
} from './ChannelSetupLayout';
import {
  Mail
} from 'lucide-react';

interface ChannelTypeUpdateDialogProps {
  isOpen: boolean;
  setOpenEmailModal: (open: boolean) => void;
  channel: Channel;
  setEmailChannels: React.Dispatch<React.SetStateAction<Channel[]>>;
}

export const ChannelTypeUpdateDialog = ({
  isOpen, setOpenEmailModal, channel, setEmailChannels
}: ChannelTypeUpdateDialogProps): JSX.Element | null => {
  const [btnLoading, setBtnLoading] = useState(false);
  const [emailUpdateObject, setEmailUpdateObject] = useState<Channel>(channel);
  const initialMode = emailModeFromFields(channel.receive_enabled, channel.send_mode);
  const [emailMode, setEmailMode] = useState<EmailMode>(initialMode.mode);
  const [replyBehavior, setReplyBehavior] = useState<EmailReplyBehavior>(initialMode.replyBehavior);
  // The card list mounts this dialog unconditionally, so closing it does NOT
  // unmount it: without this resync an abandoned selection (picked, then
  // Cancel) survives to the next open and a later unrelated save would commit
  // it — silently enabling auto-reply the user never confirmed.
  useEffect(() => {
    if (isOpen) {
      setEmailUpdateObject(channel);
      const stored = emailModeFromFields(channel.receive_enabled, channel.send_mode);
      setEmailMode(stored.mode);
      setReplyBehavior(stored.replyBehavior);
    }
  }, [isOpen, channel]);
  const {
    oragID, subOragID
  } = useParams<{ oragID: string; subOragID: string }>();
  const {
    addNewNotifcation
  } = useContext(Context);

  // The card list routes unknown channel types into this dialog too; the mode
  // axes are an email concept and must not be written onto other types.
  const isEmail = (channel.type ?? '').toLowerCase() === 'email';

  const handleOnSubmit = () => {
    setBtnLoading(true);

    const modeFields = isEmail ? emailModeToFields(emailMode, replyBehavior) : {};
    // Server update schema is strict: send only the keying fields + mutable fields.
    const emailObj: RedisChannelUpdate = {
      org_id: oragID || '',
      sub_org_id: subOragID || '',
      channelId: emailUpdateObject._id,
      topic: emailUpdateObject.topic,
      ...(emailUpdateObject.assistant_id ? {
 assistant_id: emailUpdateObject.assistant_id
} : {}),
      ...modeFields,
    };

    channelService.updateChannel(emailObj)
      .then(() => {
        // The update endpoint echoes the update params (keyed by channelId, no
        // _id), so patch the list from the values we know were saved.
        setEmailChannels(prev => prev.map(item => item._id === emailUpdateObject._id
          ? {
            ...item,
            topic: emailUpdateObject.topic,
            assistant_id: emailUpdateObject.assistant_id,
            ...modeFields,
          }
          : item));
        addNewNotifcation('Channel updated successfully', 'success');
        setOpenEmailModal(false);
      })
      .catch(error => {
        if (error.response?.status === 404) {
          addNewNotifcation('Email not found', 'danger');
        } else {
          addNewNotifcation('Something went wrong', 'danger');
        }
      })
      .finally(() => {
        setBtnLoading(false);
      });
  };

  const handleChange = <K extends keyof Channel>(value: Channel[K], type: K) => {
    setEmailUpdateObject(prev => ({
      ...prev,
      [type]: value
    }));
  };

  if (!emailUpdateObject) return null;

  return (
      <Dialog open={isOpen} onOpenChange={setOpenEmailModal}>
          <DialogContent className="p-0 sm:max-w-[860px] max-w-none overflow-y-auto max-h-[95svh] sm:max-h-[90vh]">
              <ChannelSetupLayout
          onCancel={() => setOpenEmailModal(false)}
          onSubmit={handleOnSubmit}
          submitDisabled={btnLoading}
          loading={btnLoading}
          title={isEmail ? 'Update Email Channel' : 'Update Channel'}
          isDashboard={true}
        >
                  <div className="flex flex-col sm:flex-row w-full">
                      <aside className="sm:w-[320px] shrink-0 bg-muted/40 border-b sm:border-b-0 sm:border-r border-border p-6 flex flex-col gap-5">
                          <div className="space-y-1">
                              <div className="inline-flex items-center gap-2 px-2 py-1 bg-primary/10 rounded-md text-primary mb-1">
                                  <Mail size={12} />
                                  <span className="text-[10px] font-bold uppercase tracking-wider">Channel Configuration</span>
                              </div>
                              <h3 className="text-sm font-semibold text-foreground px-1">Configure Instance</h3>
                          </div>
                          <div className="flex flex-col gap-4">
                              <div className="flex flex-col gap-1">
                                  <Label className="text-[11px] font-semibold text-muted-foreground ml-1">
                                      Display Name <span className="text-red-500">*</span>
                                  </Label>
                                  <Input
                    value={emailUpdateObject.topic ?? ''}
                    onChange={(e) => handleChange(e.target.value, 'topic')}
                    placeholder="Customer Support"
                    className="h-9 text-xs"
                  />
                              </div>
                              <div className="flex flex-col gap-1.5">
                                  <Label className="text-[11px] font-semibold text-muted-foreground ml-1">
                                      AI Assistant
                                  </Label>
                                  <AssistantSelect
                    className="mt-0.5"
                    value={emailUpdateObject.assistant_id}
                    onChange={(value) => handleChange(value, 'assistant_id')}
                  />
                              </div>
                              {isEmail && (
                              <div className="flex flex-col gap-2">
                                  <Label className="text-[11px] font-semibold text-muted-foreground ml-1">
                                      Channel Behaviour
                                  </Label>
                                  {/* Modes cannot cross the Graph-subscription boundary after
                                      creation: a subscribed mailbox cannot become send-only
                                      (its subscription would keep delivering), and a send-only
                                      mailbox has no subscription to start listening with. */}
                                  <EmailModeSettings
                                    mode={emailMode}
                                    replyBehavior={replyBehavior}
                                    onModeChange={setEmailMode}
                                    onReplyBehaviorChange={setReplyBehavior}
                                    lockedModes={emailUpdateObject.subscription_id ? ['send_only'] : ['listen', 'send_receive']}
                                  />
                              </div>
                              )}
                          </div>
                      </aside>

                      <main className="flex-grow bg-background p-8 flex flex-col items-center justify-center">
                          <div className="w-full max-w-sm space-y-6 text-center animate-in fade-in slide-in-from-bottom-2 duration-500">
                              <div className="w-16 h-16 bg-primary/10 text-primary rounded-2xl flex items-center justify-center mx-auto">
                                  <Mail size={32} />
                              </div>
                              <div className="space-y-2">
                                  <h4 className="text-lg font-bold text-foreground">Email Channel Details</h4>
                                  <p className="text-sm text-muted-foreground">
                                      The email address below is read-only and cannot be changed.
                                  </p>
                              </div>
                              <div className="flex items-center gap-2 p-3 bg-muted border border-border rounded-lg">
                                  <Mail size={14} className="text-muted-foreground shrink-0" />
                                  <code className="flex-grow text-[11px] text-muted-foreground font-mono break-all text-left">
                                      {emailUpdateObject.address}
                                  </code>
                              </div>
                          </div>
                      </main>
                  </div>
              </ChannelSetupLayout>
          </DialogContent>
      </Dialog>
  );
};
